import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, User, UserPlus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { practitionerService } from '@/services/practitionerService';
import { AnimatePresence, motion } from 'framer-motion';

// Validation schema
const invitePractitionerSchema = z.object({
  first_name: z
    .string()
    .max(100, 'First name is too long')
    .transform((value) => value.trim())
    .optional()
    .or(z.literal('')),
  last_name: z
    .string()
    .max(100, 'Last name is too long')
    .transform((value) => value.trim())
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .refine(
      (email) => !email.includes('..'),
      'Email cannot contain consecutive dots'
    )
    .refine(
      (email) => {
        // Extract TLD (everything after the last dot in the domain)
        const parts = email.split('@');
        if (parts.length !== 2) return false;

        const domain = parts[1];
        const domainParts = domain.split('.');

        // Must have at least one dot in domain (e.g., domain.com)
        if (domainParts.length < 2) return false;

        // Get the TLD (last part)
        const tld = domainParts[domainParts.length - 1];

        // TLD should be 2-6 characters and contain only letters
        // Common TLDs: com, org, net, edu, gov, io, co, etc.
        // Country codes: us, uk, de, fr, etc.
        const tldRegex = /^[a-zA-Z]{2,6}$/;

        return tldRegex.test(tld);
      },
      'Please enter a valid email address'
    )
    .transform((email) => email.trim().toLowerCase()),
});

type InvitePractitionerFormData = z.infer<typeof invitePractitionerSchema>;

interface InvitePractitionerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  onSuccess?: () => void;
}

const InvitePractitionerDialog: React.FC<InvitePractitionerDialogProps> = ({
  open,
  onOpenChange,
  clinicId,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<InvitePractitionerFormData>({
    resolver: zodResolver(invitePractitionerSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async (data: InvitePractitionerFormData) => {
    setIsLoading(true);

    try {
      await practitionerService.sendInvite(
        clinicId,
        data.email,
        data.first_name,
        data.last_name,
      );

      toast({
        title: 'Invitation Sent',
        description: `Onboarding invitation has been sent to ${data.email}`,
        variant: 'success',
      });

      // Reset form
      reset();
      onOpenChange(false);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      
      const errorMessage = err?.response?.data?.error?.message || err?.message || 'Failed to send invitation';
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      reset();
      onOpenChange(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center">
              <UserPlus className="h-6 w-6 text-brand-teal" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">Invite Practitioner</DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                Send an onboarding invitation to a new practitioner
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-sm font-medium">
                  First Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="first_name"
                    type="text"
                    placeholder="Jane"
                    {...register('first_name')}
                    className={`pl-10 ${errors.first_name ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                <AnimatePresence>
                  {errors.first_name && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-[0.8rem] text-red-500 mt-1">{errors.first_name.message}</motion.p>
                  )}
                </AnimatePresence>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-sm font-medium">
                  Last Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="last_name"
                    type="text"
                    placeholder="Doe"
                    {...register('last_name')}
                    className={`pl-10 ${errors.last_name ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                <AnimatePresence>
                  {errors.last_name && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-[0.8rem] text-red-500 mt-1">{errors.last_name.message}</motion.p>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="text"
                  placeholder="practitioner@example.com"
                  {...register('email')}
                  onChange={(e) => {
                    setValue('email', e.target.value.trim(), { shouldValidate: true });
                  }}
                  className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <AnimatePresence>

              {errors.email && (
                <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-[0.8rem] text-red-500 mt-1">{errors.email.message}</motion.p>
              )}
              </AnimatePresence>
            </div>

            <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-3">
              <h4 className="font-medium text-slate-800 text-xs mb-1.5">What happens next?</h4>
              <ul className="text-xs text-slate-600 space-y-0.5 list-disc list-inside">
                <li>Practitioner receives an invitation email</li>
                <li>They complete the onboarding form</li>
                <li>An admin approves the request to activate the account</li>
                <li>They receive portal access credentials</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-brand-teal hover:bg-brand-teal/90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvitePractitionerDialog;

